import type { ReactNode } from 'react';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Grid, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PendingRefundsAlert } from '@/features/operations/components/PendingRefundsAlert';
import { FeatureCard } from '@/components/FeatureCard';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';

type DashboardSection = {
  title: string;
  description: string;
  icon: ReactNode;
  to?: string;
};

/// The daily work: every internal role uses it.
const operationSections: DashboardSection[] = [
  {
    title: 'Cotizaciones',
    description: 'Buscar solicitudes, gestionarlas y registrar la recepción del equipo.',
    icon: <RequestQuoteOutlinedIcon color="secondary" />,
    to: '/admin/quotations',
  },
  {
    title: 'Seguimiento',
    description: 'A quién contactar hoy: cotizaciones que no avanzan y reservas activas.',
    icon: <EventNoteOutlinedIcon color="secondary" />,
    to: '/admin/follow-up',
  },
  {
    title: 'Equipos',
    description: 'Los celulares recibidos, su estado y el acceso a su ficha interna.',
    icon: <PhoneIphoneOutlinedIcon color="secondary" />,
    to: '/admin/devices',
  },
  {
    title: 'Clientes',
    description: 'Buscar una cuenta, ver y corregir sus datos, o crear una nueva.',
    icon: <PeopleAltOutlinedIcon color="secondary" />,
    to: '/admin/customers',
  },
];

/// What PhoneTec offers and how it prices it. ADMIN only, as the backend already enforces.
const configurationSections: DashboardSection[] = [
  {
    title: 'Usuarios y permisos',
    description: 'Acceso interno y permisos del equipo PhoneTec.',
    icon: <ManageAccountsOutlinedIcon color="secondary" />,
  },
  {
    title: 'Dashboard comercial',
    description: 'Indicadores de cotizaciones, equipos, reservas, ventas y cuentas.',
    icon: <AssessmentOutlinedIcon color="secondary" />,
    to: '/admin/metrics',
  },
  {
    title: 'Configuración comercial',
    description: 'Qué se pregunta al cotizar, y cuánto descuenta cada respuesta en cada celular.',
    icon: <HubOutlinedIcon color="secondary" />,
    to: '/admin/pricing',
  },
  {
    title: 'Sucursales',
    description: 'Dónde opera PhoneTec y dónde queda cada equipo recibido.',
    icon: <StorefrontOutlinedIcon color="secondary" />,
    to: '/admin/branches',
  },
  {
    title: 'Marcas del catálogo',
    description: 'Qué marcas se ofrecen en el formulario público de cotización.',
    icon: <SellOutlinedIcon color="secondary" />,
    to: '/admin/brands',
  },
  {
    title: 'Modelos del catálogo',
    description: 'Qué modelos y capacidades se aceptan en las cotizaciones.',
    icon: <PhoneAndroidOutlinedIcon color="secondary" />,
    to: '/admin/phone-models',
  },
  {
    title: 'Colores',
    description: 'La lista general de colores que después se asignan a cada modelo.',
    icon: <PaletteOutlinedIcon color="secondary" />,
    to: '/admin/colors',
  },
];

function SectionGrid({ items }: { items: DashboardSection[] }) {
  return (
    <Grid container spacing={3}>
      {items.map((item) => (
        <Grid item xs={12} md={4} key={item.title}>
          {item.to ? (
            <Link component={RouterLink} to={item.to} underline="none">
              <FeatureCard title={item.title} description={item.description} icon={item.icon} />
            </Link>
          ) : (
            <FeatureCard title={item.title} description={item.description} icon={item.icon} />
          )}
        </Grid>
      ))}
    </Grid>
  );
}

export function AdminDashboardPage() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="ADMINISTRACIÓN"
        title="Centro de control"
        description="La operación diaria de PhoneTec y, para administración, la configuración comercial y los permisos."
        badge="Uso interno"
      />
      {token ? <PendingRefundsAlert token={token} /> : null}
      <SectionCard
        title="Operación"
        description="Solicitudes de cotización, seguimiento comercial, equipos recibidos y clientes."
      >
        <SectionGrid items={operationSections} />
      </SectionCard>
      {isAdmin ? (
        <SectionCard
          title="Configuración"
          description="Lo que se ofrece y cómo se cotiza. Solo para administración."
        >
          <SectionGrid items={configurationSections} />
        </SectionCard>
      ) : null}
    </Stack>
  );
}
